import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';

import { runtimeConfig } from '../runtime-config';
import { AuthService } from '../services/auth.service';
import { SessionUserService } from '../services/session-user.service';

/**
 * Sign the user out when the tracer rejects their session.
 *
 * The bearer interceptor refreshes the Keycloak token before every tracer
 * call, so a 401 here is never a merely-expired token: the server looked at a
 * fresh one and refused the identity behind it (hub no longer knows the agent,
 * the agency was deactivated, the exchange was refused…). Leaving the app
 * logged in at that point shows a signed-in header over a page that can load
 * nothing — the v1 behaviour, which just toasted "invalid token" and stopped.
 *
 * Only a REAL session is ended. The landing page fetches its counters before
 * anyone is signed in and gets a 401 for it; reacting to that would bounce
 * every anonymous visitor through Keycloak's logout on first load.
 */
export const unauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(runtimeConfig.tracerUrl)) {
    return next(req);
  }

  const session = inject(SessionUserService);
  const auth = inject(AuthService);

  return next(req).pipe(
    tap({
      error: (err: unknown) => {
        if (!(err instanceof HttpErrorResponse) || err.status !== 401) return;
        if (!session.authenticated) return;

        const detail = typeof err.error?.error === 'string' ? err.error.error : 'session rejected';
        auth.endRejectedSession(
          `The tracer rejected your session (${detail}). You have been signed out — please sign in again.`,
        );
      },
    }),
  );
};
